import { Columns } from '../../../../widgets/columns';
import { Icon } from '../../../../widgets/icon';
import { Link } from '../../../../widgets/buttons';
import { ZTooltip } from '../../../../widgets/ztooltip';
import React, { FunctionComponent } from 'react';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { DeleteButton, EditButton, VerticalDivider } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import MdKeyboardArrowDown from 'src/shared/icons/angle-down-solid.svg';
import MdKeyboardArrowUp from 'src/shared/icons/angle-up-solid.svg';
import JSONIcon from 'src/shared/icons/json-icon.svg';
import styled, { keyframes } from 'styled-components';
import { getAllModelSides, useProductConfigurationState } from '../../state';
import { ModelColor } from '../../state/model/modelColor';
import { ModelSide } from '../../state/model/modelSide';
import VariantCell from './variantCell';

const Container = styled.div`
    margin-bottom: 40px;
`;

const Cells = styled(Columns)`
    grid-gap: 0;
`;

const VariantLabel = styled.h5`
    margin: 0;
`;

const Name = styled.div`
    margin: 0;
    margin-bottom: 15px;
    display: flex;
    align-items: center;

    ${VariantLabel} {
        flex: 1;
        font-weight: ${(props) => props.theme.text.boldWeight};
        font-size: 20px;
    }

    ${Link} {
        color: ${(props) => props.theme.app.primary};
    }
`;

const JSONIconContainer = styled(Icon)`
    width: 40px;
    height: 40px;

    svg {
        color: ${(props) => props.theme.app.primary_dark};
    }
`;

const VerticalDividerStyled = styled(VerticalDivider)`
    border-left: 1px solid rgb(219, 226, 230);
    height: 40px;
    margin-right: 16px;
`;

const blinkGlow = keyframes`
    0%, 100% {
        filter: none;
    }
    50% {
        filter: drop-shadow(0 0 6px rgba(211, 47, 47, 0.8)) drop-shadow(0 0 12px rgba(211, 47, 47, 0.4));
    }
`;

const HighlightedEditButton = styled.div`
    svg {
        color: #d32f2f;
    }
    animation: ${blinkGlow} 0.8s ease-in-out infinite;
    cursor: pointer;
    display: flex;
    align-items: center;
`;

interface VariantRowProps {
    color: ModelColor;
    isSidesPerVariantEnabled: boolean;
    showRemoveButton: boolean;
    showMoveDownButton: boolean;
    showMoveUpButton: boolean;
    showEditButton: boolean;
    onToggleClick: (colorGuid: string, sideGuid: string) => void;
    onImageChangeClick: (modelSide: ModelSide, modelColor: ModelColor) => void;
    onAreaChangeClick: (modelSide: ModelSide, modelColor: ModelColor) => void;
    onRemoveClick: (color: ModelColor) => void;
    onEditClick: (color: ModelColor) => void;
    onMoveUpClick?: () => void;
    onMoveDownClick?: () => void;
    onSetMandatorySide: (value: boolean, modelSide: ModelSide) => void;
    onSetRotateSide: (value: boolean, modelSide: ModelSide) => void;
    onClickJsonSettings: () => void;
    highlightEdit?: boolean;
}

const VariantRow: FunctionComponent<VariantRowProps> = ({
    color,
    isSidesPerVariantEnabled,
    showRemoveButton,
    showMoveDownButton,
    showMoveUpButton,
    showEditButton,
    onImageChangeClick,
    onToggleClick,
    onAreaChangeClick,
    onRemoveClick,
    onEditClick,
    onMoveUpClick,
    onMoveDownClick,
    onSetMandatorySide,
    onSetRotateSide,
    onClickJsonSettings,
    highlightEdit
}) => {
    const { state } = useProductConfigurationState();
    const { userInfo } = useAccountData();
    const isJsonProductsImportFeatureEnabled = useFeatureFlagWithConditions('jsonProductsImport', {
        userId: userInfo?.userID?.toString()
    });

    return (
        <Container>
            <Name>
                {color.colorID != 1 && <VariantLabel>{color.name}</VariantLabel>}

                {isJsonProductsImportFeatureEnabled && (
                    <>
                        <ZTooltip
                            position='top'
                            title={T._(
                                "Edit JSON settings - These settings are only needed for bulk import functionality to reference the entities in the import JSON. If you don't need the JSON bulk import functionality, you can ignore these parameters.",
                                'Admin'
                            )}
                        >
                            <JSONIconContainer $hoverable onClick={onClickJsonSettings}>
                                <JSONIcon />
                            </JSONIconContainer>
                        </ZTooltip>
                        <VerticalDividerStyled />
                    </>
                )}

                {showMoveUpButton && (
                    <Icon $hoverable onClick={onMoveUpClick}>
                        <ZTooltip title={T._('Move up', 'Admin')}>
                            <MdKeyboardArrowUp />
                        </ZTooltip>
                    </Icon>
                )}

                {showMoveDownButton && (
                    <Icon $hoverable onClick={onMoveDownClick}>
                        <ZTooltip title={T._('Move down', 'Admin')}>
                            <MdKeyboardArrowDown />
                        </ZTooltip>
                    </Icon>
                )}

                {showEditButton &&
                    (highlightEdit ? (
                        <HighlightedEditButton>
                            <EditButton onClick={() => onEditClick(color)} />
                        </HighlightedEditButton>
                    ) : (
                        <EditButton onClick={() => onEditClick(color)} />
                    ))}
                {showRemoveButton && <DeleteButton onClick={() => onRemoveClick(color)}></DeleteButton>}
            </Name>

            <Cells $xs={1} $md={4}>
                {getAllModelSides(state.product).map((x) => {
                    const modelSide = color.sides.find((j) => j.originalSideGuid == x.originalSideGuid);
                    const side = state.sides.find((j) => j.guid == x.originalSideGuid)!;

                    return (
                        <VariantCell
                            key={side.guid}
                            side={side}
                            isToggleEnabled={isSidesPerVariantEnabled}
                            isAreaChangeEnabled={isSidesPerVariantEnabled}
                            modelColor={color}
                            modelSide={modelSide}
                            onToggleClick={() => onToggleClick(color.guid, side.guid)}
                            onAreaChangeClick={() => onAreaChangeClick(modelSide!, color)}
                            onImageChangeClick={() => onImageChangeClick(modelSide!, color)}
                            onSetMandatorySide={(value) => onSetMandatorySide(value, modelSide!)}
                            onSetRotateSide={(value) => onSetRotateSide(value, modelSide!)}
                        />
                    );
                })}

                {state.sides.length % 4 > 0 &&
                    new Array(4 - (state.sides.length % 4)).fill(0).map((_, index) => {
                        return <VariantCell key={index} modelColor={color} modelSide={null} dummy={true} />;
                    })}
            </Cells>
        </Container>
    );
};

export default VariantRow;
